import { resolveGuide } from '@/lib/tour/pageGuides';
import { TOUR_STEPS } from '@/lib/tour/steps';

/**
 * <b>지금 화면에 실제로 떠 있는 자리들</b> — AI 가 「저기요」 하고 짚을 수 있는 것의 전부
 * (사용자 지시 2026-09-23: "표시해줘 이런 말을 하면 해당하는 부분을 하이라이트").
 *
 * <h2>새로 만든 것이 없다</h2>
 * 짚을 자리는 이미 173곳에 이름이 붙어 있다 — 안내 투어가 쓰는 {@code data-tour} 다. 반짝이는
 * 테두리를 그리는 것도 이미 있다({@link SpotlightRing}). 없던 것은 <b>대화가 그 이름을 아는 길</b>
 * 하나였다.
 *
 * <h2>화면이 목록을 보낸다 — 에이전트가 들고 있지 않는다</h2>
 * 화면 이름표를 프론트에서 안 받는 것(`agents/screens.py`)과 <b>반대 방향</b>의 규율이다.
 * 그쪽은 에이전트가 이미 아는 것이고, 이쪽은 <b>화면만 안다</b> — 지금 이 사람의 권한으로,
 * 이 탭에서, 이 스크롤 위치에 무엇이 떠 있는지는 브라우저의 DOM 에만 있다. 에이전트가 표를 한
 * 벌 들면 <b>권한이 없어 안 보이는 버튼을 짚으라고</b> 답하게 된다.
 *
 * <p>그래서 <b>보내는 것은 보이는 것뿐</b>이다. 목록에 없는 이름을 모델이 지어내도 그 자리에서
 * 걸러진다({@link isKnownAnchor}).
 *
 * <h2>이름이 아니라 <b>사람이 읽는 말</b>을 함께 보낸다</h2>
 * {@code docslot-ask}·{@code pmis-ai-summary} 같은 이름만 보내면 모델이 고를 근거가 없다.
 * 안내 대본이 그 자리에 붙여 둔 제목({@code pageGuides} 의 {@code spots})을 먼저 쓰고, 대본에
 * 없는 자리는 <b>그 요소에 적힌 글자</b>를 쓴다 — 버튼이면 버튼에 쓰인 말이 곧 이름이다.
 */

export type ScreenAnchor = {
  /** `data-tour` 값. 모델이 고르면 이 문자열이 그대로 돌아온다. */
  name: string;
  /** 사람이 읽는 이름. 모델은 이것을 보고 고른다. */
  label: string;
};

/** 한 번에 보낼 수 있는 자리 수. 현장 조회처럼 탭이 열 개인 화면이 40 줄쯤 된다. */
const MAX_ANCHORS = 40;

/** 이름표로 쓸 글자의 최대 길이. 카드 하나를 통째로 집으면 본문이 그대로 딸려 온다. */
const MAX_LABEL = 40;

/**
 * 대본이 붙여 둔 제목. 화면 대본({@code resolveGuide})을 먼저 보고, 없으면 전체 안내
 * ({@code TOUR_STEPS} — 사이드바·헤더처럼 <b>모든 화면에 있는 자리</b>)에서 찾는다.
 */
function labelsFromGuides(pathname: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const step of [...TOUR_STEPS, ...resolveGuide(pathname).steps]) {
    // 뒤엣것이 이긴다 — 화면 대본이 전체 안내보다 그 화면을 잘 안다.
    if (step.target) out.set(step.target, step.title);
  }
  return out;
}

/**
 * 요소에 적힌 글자. 버튼·탭은 이것이 곧 이름이다.
 *
 * <p>{@code aria-label}·{@code title} 을 먼저 본다 — 아이콘만 있는 버튼은 글자가 없고,
 * 그런 버튼일수록 "저거 뭐야" 를 듣는다.
 */
function labelFromElement(element: HTMLElement): string {
  const aria = element.getAttribute('aria-label') ?? element.getAttribute('title') ?? '';
  const text = aria.trim() || (element.textContent ?? '').replace(/\s+/g, ' ').trim();
  return text.length > MAX_LABEL ? `${text.slice(0, MAX_LABEL)}…` : text;
}

/** 화면에 실제로 보이는가. 조건은 {@link SpotlightRing} 과 같다 — 짚어 봐야 안 보이면 소용없다. */
function isVisible(element: HTMLElement): boolean {
  if (element.offsetParent === null) return false;
  const box = element.getBoundingClientRect();
  return box.width > 0 && box.height > 0;
}

/**
 * 지금 떠 있는 자리들을 걷는다. 브라우저가 없으면(테스트·SSR) 빈 목록이다.
 *
 * <p><b>스크롤 밖도 넣는다.</b> 보이는 것만 보내면 「자료 입력 어디야」 를 화면 아래에서 물었을 때
 * 그 버튼이 목록에 없어 답을 못 한다 — 짚어 주면 {@link SpotlightRing} 이 그리로 굴려 준다.
 * 거르는 것은 <b>DOM 에 없거나 크기가 0 인 것</b>(접힌 서랍·권한으로 빠진 버튼)뿐이다.
 */
export function collectScreenAnchors(pathname: string): ScreenAnchor[] {
  if (typeof document === 'undefined') return [];

  const titles = labelsFromGuides(pathname);
  const out: ScreenAnchor[] = [];
  const seen = new Set<string>();

  for (const element of Array.from(document.querySelectorAll<HTMLElement>('[data-tour]'))) {
    const name = element.dataset.tour ?? '';
    if (!name || seen.has(name) || !isVisible(element)) continue;

    const label = titles.get(name) ?? labelFromElement(element);
    // 이름표가 없는 자리는 <b>보내지 않는다</b>. 모델에게 `docslot-list` 만 주면 고를 근거가
    // 없고, 찍어서 고른 자리를 짚어 주는 것은 안 짚어 주는 것보다 나쁘다.
    if (!label) continue;

    seen.add(name);
    out.push({ name, label });
    if (out.length >= MAX_ANCHORS) break;
  }

  return out;
}

/**
 * 모델이 고른 이름이 <b>우리가 보낸 목록에 있는가</b>. 없으면 짚지 않는다.
 *
 * <p>화면 안내의 {@code href_of()} 와 같은 자리다 — 모델이 없는 화면을 고르면 그 버튼을 버리듯,
 * 없는 자리를 고르면 반짝임을 버린다. 허공을 반짝이면 사용자는 자기가 뭘 놓쳤나 하고 화면을 훑는다.
 */
export function isKnownAnchor(name: string, anchors: ScreenAnchor[]): boolean {
  return anchors.some((anchor) => anchor.name === name);
}
